import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { FileDown, Plus, Trash2, Loader2, Gift, Package } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const ADMIN_KEY = 'jnm-admin-2024';

const PAID_METHODS = [
  { value: 'bar', label: 'Bar', inline: 'bar' },
  { value: 'online', label: 'Online (z.B. PayPal)', inline: 'online' },
  { value: 'ueberweisung', label: 'Überweisung', inline: 'per Überweisung' },
];

const emptyItem = () => ({
  name: '',
  description: '',
  quantity: 1,
  unit_price: 0,
  product_ref: '', // optional reference to product in catalog
});

/**
 * Helper: extract a sensible "from price" from a product.
 * Products have either `price` (number), or `pricing.buy` (formatted string),
 * or `pricing.buy_net` etc. We try multiple paths.
 */
const readProductPrice = (p) => {
  if (!p) return 0;
  if (typeof p.price === 'number') return p.price;
  if (typeof p.price === 'string') {
    const n = Number(p.price.replace(/[^0-9.-]/g, ''));
    if (!isNaN(n)) return n;
  }
  if (p.pricing) {
    if (typeof p.pricing.buy_net === 'number') return p.pricing.buy_net;
    if (typeof p.pricing.buy === 'number') return p.pricing.buy;
    if (typeof p.pricing.buy === 'string') {
      const n = Number(p.pricing.buy.replace(/[^0-9.-]/g, ''));
      if (!isNaN(n)) return n;
    }
  }
  return 0;
};

const ManualInvoiceForm = () => {
  const [loading, setLoading] = useState(false);
  const [catalog, setCatalog] = useState({ machines: [], accessories: [] });

  const [form, setForm] = useState({
    customer_name: '',
    customer_company: '',
    customer_street: '',
    customer_zip: '',
    customer_city: '',
    customer_country: 'Deutschland',
    customer_email: '',
    customer_phone: '',
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    payment_method: 'Barzahlung',
    payment_status: 'paid',
    vat_mode: 'standard',
    notes: '',
    override_total: '', // manual override of final gross total (empty = auto calculate)
    paid_cash: '',      // partial cash payment amount (empty = no split payment)
    paid_method: 'bar',
    payment_due_days: 7, // business days to transfer the remaining balance
    items: [emptyItem()],
  });

  // Load products and accessories for the catalog picker
  useEffect(() => {
    const load = async () => {
      try {
        const [mRes, aRes] = await Promise.all([
          fetch(`${API_URL}/api/products/machines`),
          fetch(`${API_URL}/api/products/accessories`),
        ]);
        const machines = mRes.ok ? await mRes.json() : [];
        const accessories = aRes.ok ? await aRes.json() : [];
        setCatalog({ machines: machines || [], accessories: accessories || [] });
      } catch (e) {
        console.error('catalog load failed', e);
      }
    };
    load();
  }, []);

  const updateField = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const updateItem = (idx, field, value) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: value };
    setForm((f) => ({ ...f, items }));
  };

  const pickProduct = (idx, productId) => {
    if (!productId) {
      updateItem(idx, 'product_ref', '');
      return;
    }
    const all = [...catalog.machines, ...catalog.accessories];
    const p = all.find((x) => String(x.id) === String(productId) || x.slug === productId);
    if (!p) return;
    const netPrice = readProductPrice(p);
    const items = [...form.items];
    items[idx] = {
      ...items[idx],
      name: p.name || items[idx].name,
      unit_price: netPrice,
      product_ref: p.slug || String(p.id || ''),
    };
    setForm((f) => ({ ...f, items }));
  };

  const markFree = (idx) => {
    const items = [...form.items];
    items[idx] = {
      ...items[idx],
      unit_price: 0,
      description:
        items[idx].description && items[idx].description.includes('GRATIS')
          ? items[idx].description
          : (items[idx].description ? items[idx].description + ' — ' : '') + 'GRATIS als Beigabe',
    };
    setForm((f) => ({ ...f, items }));
  };

  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }));
  const removeItem = (idx) =>
    setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const subtotal = form.items.reduce(
    (sum, it) => sum + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0),
    0
  );
  const vat = form.vat_mode === 'standard' ? subtotal * 0.19 : 0;
  const calculatedTotal = subtotal + vat;
  const overrideNum = Number(form.override_total);
  const hasOverride = form.override_total !== '' && overrideNum > 0;
  const total = hasOverride ? overrideNum : calculatedTotal;
  // When override is active, back-calculate subtotal and VAT proportionally
  const displaySubtotal = hasOverride
    ? form.vat_mode === 'standard' ? overrideNum / 1.19 : overrideNum
    : subtotal;
  const displayVat = hasOverride
    ? form.vat_mode === 'standard' ? overrideNum - overrideNum / 1.19 : 0
    : vat;

  // Split payment (Teilzahlung) preview
  const paidCashNum = Number(form.paid_cash);
  const hasPaidCash = form.paid_cash !== '' && paidCashNum > 0;
  const remainingDue = hasPaidCash ? Math.max(0, total - paidCashNum) : 0;
  const isSplitPayment = hasPaidCash && remainingDue > 0.009 && paidCashNum < total;
  const paidMethodLabel = PAID_METHODS.find((m) => m.value === form.paid_method).inline;

  const handleGenerate = async () => {
    if (!form.customer_name.trim()) {
      toast.error('Bitte Kundenname eingeben');
      return;
    }
    if (!form.items.length || form.items.some((it) => !it.name.trim())) {
      toast.error('Bitte mindestens eine Position mit Bezeichnung eingeben');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...form,
        override_total: form.override_total !== '' && Number(form.override_total) > 0
          ? Number(form.override_total)
          : null,
        paid_cash: isSplitPayment ? paidCashNum : null,
        payment_due_days: Number(form.payment_due_days) || 7,
        items: form.items.map((it) => ({
          name: it.name,
          description: it.description,
          quantity: Number(it.quantity) || 1,
          unit_price: Number(it.unit_price) || 0,
        })),
      };

      const response = await fetch(`${API_URL}/api/admin/manual-invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Key': ADMIN_KEY,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'PDF-Erzeugung fehlgeschlagen');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = form.customer_name.replace(/\s+/g, '_').slice(0, 40);
      a.download = `Rechnung_${form.invoice_number || 'manuell'}_${safeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('Rechnung heruntergeladen!');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="manual-invoice-form">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Manuelle Rechnung erstellen</h2>
        <p className="text-sm text-gray-500 mt-1">
          Für Verkäufe aus der Halle oder andere Verkäufe außerhalb der Website. Preise und Positionen
          sind frei editierbar. Sie können aus Ihrem Katalog auswählen oder alles manuell eingeben.
        </p>
      </div>

      {/* Kunde */}
      <div className="bg-white rounded-lg border p-4 sm:p-6 space-y-4">
        <h3 className="font-semibold text-gray-900">Kundendaten</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Name *</Label>
            <Input
              value={form.customer_name}
              onChange={(e) => updateField('customer_name', e.target.value)}
              placeholder="Max Mustermann"
              data-testid="input-customer-name"
            />
          </div>
          <div>
            <Label>Firma (optional)</Label>
            <Input
              value={form.customer_company}
              onChange={(e) => updateField('customer_company', e.target.value)}
              placeholder="Mustermann Bau GmbH"
            />
          </div>
          <div className="sm:col-span-2">
            <Label>Straße und Hausnummer *</Label>
            <Input
              value={form.customer_street}
              onChange={(e) => updateField('customer_street', e.target.value)}
              placeholder="Musterstraße 12"
            />
          </div>
          <div>
            <Label>PLZ *</Label>
            <Input
              value={form.customer_zip}
              onChange={(e) => updateField('customer_zip', e.target.value)}
              placeholder="46236"
            />
          </div>
          <div>
            <Label>Ort *</Label>
            <Input
              value={form.customer_city}
              onChange={(e) => updateField('customer_city', e.target.value)}
              placeholder="Bottrop"
            />
          </div>
          <div>
            <Label>Land</Label>
            <Input
              value={form.customer_country}
              onChange={(e) => updateField('customer_country', e.target.value)}
            />
          </div>
          <div>
            <Label>E-Mail (optional)</Label>
            <Input
              type="email"
              value={form.customer_email}
              onChange={(e) => updateField('customer_email', e.target.value)}
              placeholder="kunde@email.de"
            />
          </div>
          <div>
            <Label>Telefon (optional)</Label>
            <Input
              value={form.customer_phone}
              onChange={(e) => updateField('customer_phone', e.target.value)}
              placeholder="0176 12345678"
            />
          </div>
        </div>
      </div>

      {/* Rechnungs-Metadaten */}
      <div className="bg-white rounded-lg border p-4 sm:p-6 space-y-4">
        <h3 className="font-semibold text-gray-900">Rechnungsdetails</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Rechnungsnummer (leer = automatisch)</Label>
            <Input
              value={form.invoice_number}
              onChange={(e) => updateField('invoice_number', e.target.value)}
              placeholder="z.B. R-2026-001"
            />
          </div>
          <div>
            <Label>Rechnungsdatum</Label>
            <Input
              type="date"
              value={form.invoice_date}
              onChange={(e) => updateField('invoice_date', e.target.value)}
            />
          </div>
          <div>
            <Label>Zahlungsart</Label>
            <select
              value={form.payment_method}
              onChange={(e) => updateField('payment_method', e.target.value)}
              className="w-full h-10 px-3 border border-gray-300 rounded-md text-sm bg-white"
            >
              <option value="Barzahlung">Barzahlung</option>
              <option value="EC-Karte">EC-Karte</option>
              <option value="Überweisung">Überweisung</option>
              <option value="PayPal">PayPal</option>
              <option value="Finanzierung">Finanzierung</option>
              <option value="Sonstige">Sonstige</option>
            </select>
          </div>
          <div>
            <Label>Zahlungsstatus</Label>
            <select
              value={form.payment_status}
              onChange={(e) => updateField('payment_status', e.target.value)}
              className="w-full h-10 px-3 border border-gray-300 rounded-md text-sm bg-white"
              data-testid="select-payment-status"
            >
              <option value="paid">Bezahlt</option>
              <option value="partial">Teilbezahlt</option>
              <option value="pending">Offen</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <Label>Umsatzsteuer</Label>
            <select
              value={form.vat_mode}
              onChange={(e) => updateField('vat_mode', e.target.value)}
              className="w-full h-10 px-3 border border-gray-300 rounded-md text-sm bg-white"
            >
              <option value="standard">Standard — 19% MwSt. (Preise netto)</option>
              <option value="none">Keine Umsatzsteuer (Kleinunternehmer / EU-Reverse-Charge)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Positionen */}
      <div className="bg-white rounded-lg border p-4 sm:p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Positionen</h3>
          <Button variant="outline" size="sm" onClick={addItem} data-testid="add-item-btn">
            <Plus className="h-4 w-4 mr-1" />
            Position
          </Button>
        </div>

        {form.items.map((item, idx) => (
          <div key={idx} className="border rounded-lg p-3 space-y-3 bg-gray-50">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-gray-500">Position {idx + 1}</span>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => markFree(idx)}
                  className="h-8 text-xs text-green-700 border-green-300 hover:bg-green-50"
                  title="Preis auf 0 € setzen und als Gratis-Beigabe markieren"
                >
                  <Gift className="h-3.5 w-3.5 mr-1" />
                  Gratis
                </Button>
                {form.items.length > 1 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeItem(idx)}
                    className="h-8 w-8 text-red-600"
                    title="Position entfernen"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>

            {/* Katalog-Auswahl */}
            <div>
              <Label className="flex items-center gap-1">
                <Package className="h-3.5 w-3.5" />
                Aus Katalog wählen (optional)
              </Label>
              <select
                value={item.product_ref}
                onChange={(e) => pickProduct(idx, e.target.value)}
                className="w-full h-10 px-3 border border-gray-300 rounded-md text-sm bg-white"
              >
                <option value="">— Manuell eingeben —</option>
                {catalog.machines.length > 0 && (
                  <optgroup label="Baumaschinen">
                    {catalog.machines.map((p) => (
                      <option key={`m-${p.id || p.slug}`} value={p.slug || p.id}>
                        {p.name}
                      </option>
                    ))}
                  </optgroup>
                )}
                {catalog.accessories.length > 0 && (
                  <optgroup label="Zubehör">
                    {catalog.accessories.map((p) => (
                      <option key={`a-${p.id || p.slug}`} value={p.slug || p.id}>
                        {p.name}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Wählen Sie ein Produkt — Name und Preis werden automatisch eingetragen. Sie können alles überschreiben.
              </p>
            </div>

            <div>
              <Label>Bezeichnung *</Label>
              <Input
                value={item.name}
                onChange={(e) => updateItem(idx, 'name', e.target.value)}
                placeholder="z.B. Minibagger QW12S"
              />
            </div>
            <div>
              <Label>Beschreibung (optional)</Label>
              <Textarea
                value={item.description}
                onChange={(e) => updateItem(idx, 'description', e.target.value)}
                placeholder="z.B. Inkl. Grabschaufel 30cm, 2 Jahre Gewährleistung"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Menge</Label>
                <Input
                  type="number"
                  min="1"
                  value={item.quantity}
                  onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                />
              </div>
              <div>
                <Label>Einzelpreis (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={item.unit_price}
                  onChange={(e) => updateItem(idx, 'unit_price', e.target.value)}
                  placeholder="8500.00"
                />
              </div>
            </div>
            <div className="text-right text-sm font-semibold text-gray-700">
              Zwischensumme:{' '}
              {((Number(item.quantity) || 0) * (Number(item.unit_price) || 0)).toLocaleString('de-DE', {
                minimumFractionDigits: 2,
              })}{' '}
              €
            </div>
          </div>
        ))}
      </div>

      {/* Summen-Vorschau */}
      <div className="bg-gradient-to-br from-gray-50 to-white rounded-lg border p-4 sm:p-6 space-y-3">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Netto-Summe:</span>
          <span className="font-medium">
            {displaySubtotal.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
          </span>
        </div>
        {form.vat_mode === 'standard' && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">zzgl. 19% MwSt.:</span>
            <span className="font-medium">
              {displayVat.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
            </span>
          </div>
        )}
        <div className="border-t pt-2 flex justify-between text-base font-bold">
          <span>Gesamtbetrag:</span>
          <span className="text-red-600">
            {total.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
          </span>
        </div>

        {/* Optional: Override total */}
        <div className="border-t pt-3 space-y-1">
          <Label className="text-xs font-medium text-gray-700">
            Gesamtbetrag überschreiben (optional)
          </Label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              step="0.01"
              min="0"
              value={form.override_total}
              onChange={(e) => updateField('override_total', e.target.value)}
              placeholder={`${calculatedTotal.toFixed(2)} (automatisch berechnet)`}
              className="flex-1"
            />
            {form.override_total && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => updateField('override_total', '')}
                className="text-xs"
              >
                Zurücksetzen
              </Button>
            )}
          </div>
          <p className="text-xs text-gray-500">
            Bei Preisverhandlung z.B. „10.000,00 € brutto" eintragen. MwSt. wird automatisch rausgerechnet. Leer lassen = automatisch aus Positionen berechnen.
          </p>
        </div>

        {/* Split payment (Teilzahlung) */}
        <div className="border-t pt-3 space-y-2">
          <Label className="text-xs font-semibold text-gray-800">
            Teilzahlung – bereits bezahlt (optional)
          </Label>
          <div className="flex flex-wrap gap-2" data-testid="paid-method-group">
            {PAID_METHODS.map((m) => (
              <button
                key={m.value}
                type="button"
                onClick={() => updateField('paid_method', m.value)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  form.paid_method === m.value
                    ? 'bg-red-600 text-white border-red-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-red-400'
                }`}
                data-testid={`paid-method-${m.value}`}
              >
                {m.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-gray-600">Bereits erhalten – {paidMethodLabel} (€)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={form.paid_cash}
                onChange={(e) => setForm((f) => ({
                  ...f,
                  paid_cash: e.target.value,
                  payment_status: Number(e.target.value) > 0 && f.payment_status === 'paid' ? 'partial' : f.payment_status,
                }))}
                placeholder="z.B. 9000.00"
                data-testid="input-paid-cash"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-600">Restbetrag fällig in (Werktagen)</Label>
              <Input
                type="number"
                min="1"
                max="60"
                value={form.payment_due_days}
                onChange={(e) => updateField('payment_due_days', e.target.value)}
                data-testid="input-payment-due-days"
              />
            </div>
          </div>
          {isSplitPayment && (
            <div
              className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm space-y-1"
              data-testid="split-payment-preview"
            >
              <div className="flex justify-between">
                <span className="text-gray-700">Bereits bezahlt ({paidMethodLabel}):</span>
                <span className="font-semibold text-green-700">
                  {paidCashNum.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Restbetrag (per Überweisung in {form.payment_due_days || 7} Werktagen):</span>
                <span className="font-bold text-red-600">
                  {remainingDue.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                </span>
              </div>
              <p className="text-xs text-amber-800 pt-1 border-t border-amber-200">
                Auf der Rechnung wird ein gelber Hinweiskasten mit Bankdaten und Restbetrag eingefügt.
              </p>
            </div>
          )}
          {hasPaidCash && paidCashNum >= total && total > 0 && (
            <p className="text-xs text-green-700">
              Vollständig bezahlt ({paidMethodLabel}) – kein Restbetrag offen.
            </p>
          )}
          <p className="text-xs text-gray-500">
            Beispiel: Gesamtbetrag 9.200 €, Kunde hat 9.000 € bezahlt → 200 € werden als Restbetrag ausgewiesen. Leer lassen wenn keine Teilzahlung.
          </p>
        </div>
      </div>

      {/* Notizen */}
      <div className="bg-white rounded-lg border p-4 sm:p-6 space-y-2">
        <Label>Hinweise auf der Rechnung (optional)</Label>
        <Textarea
          value={form.notes}
          onChange={(e) => updateField('notes', e.target.value)}
          placeholder="z.B. Abholung direkt aus der Halle. Vielen Dank für Ihren Einkauf!"
          rows={3}
        />
      </div>

      <Button
        onClick={handleGenerate}
        disabled={loading}
        className="w-full bg-red-600 hover:bg-red-700 h-12 text-base"
        data-testid="generate-invoice-btn"
      >
        {loading ? (
          <>
            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
            Erzeuge PDF...
          </>
        ) : (
          <>
            <FileDown className="h-5 w-5 mr-2" />
            Rechnung als PDF herunterladen
          </>
        )}
      </Button>
    </div>
  );
};

export default ManualInvoiceForm;
